import React, { useCallback, useEffect, useState } from 'react';
import { CardMedia, Grid } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SnackbarAlert, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { EnvironmentDTO, EnvironmentFilterDTO, ReleaseAppDTO, ReleaseAppFilterDTO, TestAgentDTO, TestAgentFilterDTO, TestComponentDTO, TestComponentFilterDTO, TestExecutionDTO, TestLogDTO, UserDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';

interface ITestLogViewProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
}

export const TestLogView: React.FC<ITestLogViewProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);
    const [testComponentList, setTestComponentList] = useState<TestComponentDTO[]>([]);
    const [testAgentList, setTestAgentList] = useState<TestAgentDTO[]>([]);
    const [releaseAppList, setReleaseAppList] = useState<ReleaseAppDTO[]>([]);
    const [environmentList, setEnvironmentList] = useState<EnvironmentDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [testLogDTO, setTestLogDTO] = useState<TestLogDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestLogDTO({ 
            ...testLogDTO, 
            testExecutionId: parentId ?? '' 
        } as TestLogDTO);

        if(isOpenMaintenance){
            handleTestComponentGetAllAsync();
            handleStatusExecutionGetAllAsync();
            handleTestAgentGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleStatusExecutionGetAllAsync = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.testingsphere.testpackage.executionstatus,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setExecutionStatusList, showMessage);
    }, []);

    const handleTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setTestComponentList, showMessage);
    }, []);

    const handleTestAgentGetAllAsync = useCallback(async () => {
        const testAgentFilterDTO: TestAgentFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('testingsphere.testagent', testAgentFilterDTO, setLoading, setTestAgentList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('testingsphere.testlog', data.id, setLoading, setTestLogDTO, showMessage);
    }, [data]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'close', text: 'Close', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='TEST LOG  DETAILS'
                icon='list_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={10}>
                            <TextFieldReadOnly
                                id={'name'}
                                label='Name'
                                value={testLogDTO?.name}
                                hasToolTip={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'executionStatusId'}
                                label='Status Execution'
                                value={(<ChipStatus label='Execution Status' value={String(executionStatusList.find(x => x.id === testLogDTO?.executionStatusId)?.value || 'No Run')} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'description'}
                                label='Description'
                                value={testLogDTO?.description}
                                hasDivider={true}
                                hasToolTip={false}
                                noWrap
                            />
                        </Grid>
                        {testLogDTO?.evidencePath && (
                            <Grid item xs={12} md={12}>
                                <CardMedia sx={{ borderRadius: 1 }} component='img' src={`data:image/png;base64, ${testLogDTO?.evidencePath}`} />
                            </Grid>
                        )}

                        {testLogDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={testLogDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === testLogDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(testLogDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === testLogDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(testLogDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};